/**
 * Results screen — shown after completing a level.
 *
 * Displays score breakdown, star count, and options to retry or exit.
 */

import type { ScoreBreakdown } from '@/game/scoring';
import type { CompletionResult } from '@/sync';

export class ResultsScreen {
  private element: HTMLElement | null = null;
  onRetry: (() => void) | null = null;
  onExit: (() => void) | null = null;

  show(
    breakdown: ScoreBreakdown,
    levelName: string,
    timeMs: number,
    isNewBest: boolean,
    completionResult: CompletionResult | null,
  ): void {
    this.hide();

    const el = document.createElement('div');
    el.id = 'results-screen';
    el.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(10, 10, 26, 0.92);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      z-index: 60; backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      font-family: 'Courier New', monospace;
      animation: fadeIn 0.3s ease-out;
    `;

    const stars = '★'.repeat(breakdown.starCount) + '☆'.repeat(3 - breakdown.starCount);
    const timeSec = (timeMs / 1000).toFixed(1);

    const verifierBanner = completionResult?.isVerifier
      ? `<div id="results-verifier-banner" style="
            color: #00ffff; font-size: clamp(0.9rem, 2.5vw, 1.1rem); font-weight: bold;
            text-shadow: 0 0 16px #00ffff, 0 0 32px #00ffff66; letter-spacing: 0.1em;
            margin-bottom: 0.5rem; animation: verifierPulse 2s ease-out;
            background: rgba(0, 255, 255, 0.08); border: 1px solid #00ffff66;
            border-radius: 4px; padding: 8px 16px;">
          🏆 LEVEL VERIFIED — you're the first to complete this version!
         </div>`
      : '';

    const playCount = completionResult
      ? `<div id="results-play-count" style="color: #888; font-size: 0.7rem; margin-top: 0.5rem;">
          Played ${completionResult.count} ${completionResult.count === 1 ? 'time' : 'times'}
         </div>`
      : '';

    el.innerHTML = `
      ${verifierBanner}
      <div style="color: #00ffff; font-size: clamp(1.2rem, 4vw, 2rem); font-weight: bold;
                  text-shadow: 0 0 20px #00ffff, 0 0 40px #00ffff44;
                  letter-spacing: 0.1em; margin-bottom: 0.5rem;">
        LEVEL COMPLETE
      </div>
      <div style="color: #ff00ff; font-size: clamp(0.8rem, 2.5vw, 1.1rem); margin-bottom: 1.5rem;">
        ${levelName}
      </div>
      <div style="color: #ffdd33; font-size: clamp(1.5rem, 4vw, 2.2rem); margin-bottom: 1rem;
                  text-shadow: 0 0 15px #ffdd3388;">
        ${stars}
      </div>
      <div style="width: 260px; max-width: 80vw;">
        ${this.scoreLine('Completion', breakdown.completionScore)}
        ${this.scoreLine('Stars', breakdown.starBonus, `×${breakdown.starCount}`)}
        ${this.scoreLine('Time', breakdown.timeBonus, timeSec + 's')}
        ${this.scoreLine('Elegance', breakdown.eleganceBonus, breakdown.eleganceTier)}
        ${breakdown.fragmentCount > 0 ? this.scoreLine('Fragments', breakdown.fragmentBonus, `×${breakdown.fragmentCount}`) : ''}
        <div style="border-top: 1px solid #00ffff44; margin: 8px 0; padding-top: 8px;">
          ${this.scoreLine('Subtotal', breakdown.subtotal)}
          ${this.scoreLine('Convergence', '', `×${breakdown.convergenceMultiplier.toFixed(1)}`)}
        </div>
        <div style="color: #00ffff; font-size: clamp(1rem, 3vw, 1.4rem); font-weight: bold;
                    text-align: center; margin: 12px 0;
                    text-shadow: 0 0 20px #00ffff88;">
          ${breakdown.finalScore.toLocaleString()}
          ${isNewBest ? '<span style="color: #ffdd33; font-size: 0.7em;"> NEW BEST!</span>' : ''}
        </div>
      </div>
      ${playCount}
      <div style="display: flex; gap: 12px; margin-top: 1rem;">
        <button id="results-retry" class="menu-btn" style="width: 140px;">RETRY</button>
        <button id="results-exit" class="menu-btn menu-btn-exit" style="width: 140px;">MENU</button>
      </div>
    `;

    if (!document.getElementById('verifierPulseKeyframes')) {
      const style = document.createElement('style');
      style.id = 'verifierPulseKeyframes';
      style.textContent = `@keyframes verifierPulse {
        0%   { transform: scale(0.95); opacity: 0; }
        50%  { transform: scale(1.05); opacity: 1; }
        100% { transform: scale(1.00); opacity: 1; }
      }`;
      document.head.appendChild(style);
    }

    document.body.appendChild(el);
    this.element = el;

    const stopProp = (e: Event) => e.stopPropagation();
    el.querySelector('#results-retry')?.addEventListener('pointerdown', (e) => {
      stopProp(e);
      this.hide();
      this.onRetry?.();
    });
    el.querySelector('#results-exit')?.addEventListener('pointerdown', (e) => {
      stopProp(e);
      this.hide();
      this.onExit?.();
    });
  }

  hide(): void {
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }

  private scoreLine(label: string, value: number | string, detail = ''): string {
    const valStr = typeof value === 'number' ? value.toLocaleString() : value;
    return `
      <div style="display: flex; justify-content: space-between; color: #aaeeff;
                  font-size: clamp(0.7rem, 2vw, 0.85rem); margin: 3px 0;">
        <span>${label} ${detail ? `<span style="color:#00ffff88">${detail}</span>` : ''}</span>
        <span style="color: #fff">${valStr}</span>
      </div>
    `;
  }
}
